import { calculateFutureDate } from "./date-utils"
import { FundReleaseType, InstallerDocMap, getCurrentFundRelease } from "./loan-application"
import {
  IChecklistItem,
  ChecklistItemStatus,
  ChecklistItemType,
  FundRelaseGroup,
  getDisbursedDateFromLabel,
  getLatestDocTimestamp,
  getStatusByBankerComments,
  hasChecklistValue,
  isInspectionRequestChecklistItem,
  isSolarMonitoringChecklistItem,
} from "./checklist-item"
import { AgentDocResult, SolarDocStatus } from "./document-types"
import { StatusData } from "./status-data"
import { SolarMonitoringCheckKind } from "./solar-monitoring"

export const FUND_RELEASE_TYPES = [
  "Roof Funding Release",
  "Initial Funding Release",
  "Final Funding Release",
  "Full Funding Release"
]

export const getDocStatusAndAgentResult = (
  installerDocs: InstallerDocMap,
  docType: string
): { status?: string; agentResult?: string, agentComments?: string, installerResponse?: string } | undefined => {
  if (!installerDocs || typeof installerDocs !== "object") return undefined

  for (const stageKey of Object.keys(installerDocs)) {
    const stageDocs = installerDocs[stageKey]
    if (!stageDocs || typeof stageDocs !== "object") continue

    for (const docKey of Object.keys(stageDocs)) {
      const doc = stageDocs[docKey]
      if (doc?.docType === docType) {
        return {
          status: doc.status,
          agentResult: doc.agentResult,
          agentComments: doc.agentComments,
          installerResponse: doc.installerResponse
        }
      }
    }
  }
  return undefined
}

export const mapAgentResultAndStatusToChecklistStatus = (
  docStatusAndAgentResult,
  hasUpload = false
): ChecklistItemStatus => {
  const { agentResult, status, installerResponse } = docStatusAndAgentResult
  
  // Handle agent results - only show NeedsAction for AwaitingQuestion/Deny when NO installer response
  if (agentResult) {
    switch (agentResult) {
      case AgentDocResult.Approve:
      case AgentDocResult.Error:
        return ChecklistItemStatus.Completed
      case AgentDocResult.AwaitingQuestion:
      case AgentDocResult.Deny:
        // Only show NeedsAction if there's no installer response
        return installerResponse ? ChecklistItemStatus.Completed : ChecklistItemStatus.NeedsAction
      case AgentDocResult.Running:
        return ChecklistItemStatus.InProgress
      default:
        break
    }
  }

  if (status) {
    switch (status) {
      case SolarDocStatus.Submitted:
      case SolarDocStatus.Review:
      case SolarDocStatus.Accepted:
      case SolarDocStatus.Approved:
      case SolarDocStatus.Uploaded:
        return ChecklistItemStatus.Completed
      case SolarDocStatus.Comments:
      case SolarDocStatus.PendingUpload:
        return ChecklistItemStatus.NeedsAction
    }
  }

  return hasUpload ? ChecklistItemStatus.InProgress : ChecklistItemStatus.NeedsAction
}

export const getMappedDocChecklistStatus = (
  installerDocs: any,
  docType: string
): ChecklistItemStatus | undefined => {
  const docInfo = getDocStatusAndAgentResult(installerDocs, docType)
  return docInfo ? mapAgentResultAndStatusToChecklistStatus(docInfo) : undefined
}

export const isFundReleaseExpired = (requestedAt?: Date): boolean => {
  if (!requestedAt) return false
  const expires = calculateFutureDate(requestedAt, 7)
  const now = new Date()
  return now > expires
}

// Gets the date for a loan funding checklist item
export const getLoanFundingChecklistItemDate = (item: IChecklistItem, project: any): Date | String | undefined => {
  if (item?.type === ChecklistItemType.DocumentUpload) {
    return getLatestDocTimestamp(project?.uploads, item.label)
  }

  if (item?.type === ChecklistItemType.Airtable) {
    // Check if this is a Disbursed item
    if (/Disbursed$/i.test(item.label)) {
      return getDisbursedDateFromLabel(item.label, project)
    }
    
    // For other Airtable items, return the dataField value
    return project[item.dataField]
  }

  if (item?.type === ChecklistItemType.FundingRequest) {
    const fundReleaseType = item.fundReleaseTypes?.[0]
    if (!fundReleaseType) return undefined

    const currentFundRelease = getCurrentFundRelease(project, fundReleaseType)
    if (!currentFundRelease) return undefined

    if (/Requested$/i.test(item.label)) {
      return currentFundRelease.requestedAt
    }

    if (/Approved$/i.test(item.label)) {
      return currentFundRelease.agreementSigned?.timestamp
    }

    return undefined
  }

  if (isInspectionRequestChecklistItem(item)) {
    return project[item.dataField] || project.lenderInspectionRequestedTimestamp
  }

  return project[item.dataField]
}

const getFundingRequestStatus = (requestedAt?: Date, agrees?: boolean) => {
  if (!requestedAt) return ChecklistItemStatus.NeedsAction

  // Checks if 7 days have passed and user hasn't agreed
  if (!agrees && isFundReleaseExpired(requestedAt)) {
    return ChecklistItemStatus.NeedsAction
  }
  return ChecklistItemStatus.Completed
}

const getFundingDisbursedStatus = (label: string, project: any) => {
  const date = getDisbursedDateFromLabel(label, project)

  if (date) {
    return ChecklistItemStatus.Completed
  }

  // For the bank
  const stepDocStatus = project.getStepDocStatus?.() || {}
  const allDocsComplete = stepDocStatus.allItemsComplete || false

  if (allDocsComplete) {
    return ChecklistItemStatus.InProgress
  }

  return ''
}

export const getSolarMonitoringCheckKind = (
  item: Pick<IChecklistItem, 'fundReleaseTypes'>,
): SolarMonitoringCheckKind => (
  item.fundReleaseTypes?.includes(FundReleaseType.FinalFundingRelease) &&
  !item.fundReleaseTypes.includes(FundReleaseType.InitialFundingRelease)
    ? SolarMonitoringCheckKind.Production
    : SolarMonitoringCheckKind.Connectivity
)

export const getLoanFundingChecklistItemStatus = (
  item: IChecklistItem,
  project: StatusData
): ChecklistItemStatus | string => {
  // 1. Banker comments always take priority
  const bankerCommentStatus = getStatusByBankerComments(project, item.dataField)
  if (bankerCommentStatus) return bankerCommentStatus

  // 2. Document Upload items
  if (item?.type === ChecklistItemType.DocumentUpload) {
    const docInfo = getDocStatusAndAgentResult(project?.installerDocs, item.label) || {}
    const hasUpload = Boolean(getLatestDocTimestamp(project?.uploads, item.label))
    return mapAgentResultAndStatusToChecklistStatus(docInfo, hasUpload)
  }

  if (isSolarMonitoringChecklistItem(item)) {
    return project.monitoringSiteId
      ? ChecklistItemStatus.InProgress
      : ChecklistItemStatus.NeedsAction
  }

  if (isInspectionRequestChecklistItem(item)) {
    return hasChecklistValue(getLoanFundingChecklistItemDate(item, project))
      ? ChecklistItemStatus.Completed
      : ChecklistItemStatus.NeedsAction
  }

  // 3. Airtable items (including Disbursed items)
  if (item?.type === ChecklistItemType.Airtable) {
    // Check if this is a Disbursed item
    if (/Disbursed$/i.test(item.label)) {
      return getFundingDisbursedStatus(item.label, project)
    }
    
    // For other Airtable items, check if the dataField has a value
    return hasChecklistValue(project[item.dataField]) ? ChecklistItemStatus.Completed : ''
  }

  // 4. Funding Request items (Requested and Approved)
  if (item?.type === ChecklistItemType.FundingRequest) {
    const fundReleaseType = item.fundReleaseTypes?.[0]
    if (!fundReleaseType) return ''

    const currentFundRelease = getCurrentFundRelease(project, fundReleaseType)
    const { requestedAt, agreementSigned } = currentFundRelease || {}
    const agrees = Boolean(agreementSigned?.agrees)

    if (/Requested$/i.test(item.label)) {
      return getFundingRequestStatus(requestedAt, agrees)
    }

    if (/Approved$/i.test(item.label)) {
      if (!requestedAt) return ''
      return agrees ? ChecklistItemStatus.Completed : ChecklistItemStatus.NeedsAction
    }

    return ''
  }

  return ''
}

const isGroupCompleted = (group: FundRelaseGroup, project: StatusData) => {
  const disbursed = group.checklistItems.find(item =>
    item.type === ChecklistItemType.Airtable && /disbursed$/i.test(item.label))

  if (!disbursed) return false

  const status = getLoanFundingChecklistItemStatus(disbursed, project)
  return status === ChecklistItemStatus.Completed
}

const getActiveGroupIndex = (groups: FundRelaseGroup[], project: any) => {
  // Find the first group that is NOT completed
  const firstIncomplete = groups.findIndex(g => !isGroupCompleted(g, project))

  // If all completed, return -1 (no active; all done)
  return firstIncomplete === -1 ? -1 : firstIncomplete
}

export const getLoanFundingReleaseGroups = (
  groups: FundRelaseGroup[],
  project: any
) => {
  const nTPGiven = Boolean(project?.nTPGiven)
  const activeIdx = getActiveGroupIndex(groups, project)

  return groups.map((g, idx) => {
    const completed = isGroupCompleted(g, project)
    const active = nTPGiven && idx === activeIdx
    const disabled = !completed && !active // completed groups are not gray; only inactive/incomplete are

    return { group: g, completed, active, disabled }
  })
}